import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { createAdminInvitationCode, deleteAdminInvitationCode, getAdminInvitationCodes, updateAdminInvitationCode } from '../../api';
import { AdminInvitationCode } from '../../types';
import { AdminLayout } from '../../components/admin/AdminLayout';

export function AdminInvitations() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [codes, setCodes] = useState<AdminInvitationCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createMaxUses, setCreateMaxUses] = useState('1');
  const [createUnlimited, setCreateUnlimited] = useState(false);
  const [createNote, setCreateNote] = useState('');
  const [createExpiresAt, setCreateExpiresAt] = useState('');
  const [editingCode, setEditingCode] = useState<AdminInvitationCode | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      loadCodes();
    }
  }, [isAuthenticated, user]);

  const loadCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminInvitationCodes();
      setCodes(data.codes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invitation codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError(null);
      const maxUses = createUnlimited ? null : Number(createMaxUses || 1);
      const expiresAt = createExpiresAt ? new Date(createExpiresAt).toISOString() : undefined;
      const result = await createAdminInvitationCode({
        maxUses,
        note: createNote || undefined,
        expiresAt,
      });
      setCodes(prev => [result.code, ...prev]);
      setCreateNote('');
      setCreateMaxUses('1');
      setCreateUnlimited(false);
      setCreateExpiresAt('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invitation code');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingCode) return;
    try {
      setIsSaving(true);
      setError(null);
      const updated = await updateAdminInvitationCode(editingCode.id, {
        maxUses: editingCode.maxUses,
        note: editingCode.note || undefined,
        expiresAt: editingCode.expiresAt || null,
      });
      setCodes(prev => prev.map(code => (code.id === updated.code.id ? updated.code : code)));
      setEditingCode(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update invitation code');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAdminInvitationCode(id);
      setCodes(prev => prev.filter(code => code.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete invitation code');
    }
  };

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—');

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <AdminLayout
      title="Invitation Codes"
      breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Invitations' }]}
  actions={<span className="text-dark-400">{codes.length} total</span>}
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-dark-800 border border-dark-600 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-dark-50">Create New Code</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <label className="block">
              <span className="text-sm text-dark-300">Max uses</span>
              <input
                type="number"
                min={1}
                disabled={createUnlimited}
                value={createMaxUses}
                onChange={(e) => setCreateMaxUses(e.target.value)}
                className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
              />
            </label>
            <label className="block">
              <span className="text-sm text-dark-300">Expires</span>
              <input
                type="date"
                value={createExpiresAt}
                onChange={(e) => setCreateExpiresAt(e.target.value)}
                className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-sm text-dark-300">Note</span>
              <input
                type="text"
                value={createNote}
                onChange={(e) => setCreateNote(e.target.value)}
                className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-dark-300">
            <input
              type="checkbox"
              checked={createUnlimited}
              onChange={(e) => setCreateUnlimited(e.target.checked)}
              className="accent-primary-500"
            />
            Unlimited uses
          </label>
          <div className="flex justify-end">
            <button onClick={handleCreate} disabled={creating} className="btn-primary disabled:opacity-50">
              {creating ? 'Creating...' : 'Create Code'}
            </button>
          </div>
        </div>

        <div className="bg-dark-800 border border-dark-600 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-dark-700">
              <tr>
                <th className="text-left px-4 py-3 text-dark-300 font-medium">Code</th>
                <th className="text-left px-4 py-3 text-dark-300 font-medium">Uses</th>
                <th className="text-left px-4 py-3 text-dark-300 font-medium">Expires</th>
                <th className="text-left px-4 py-3 text-dark-300 font-medium">Note</th>
                <th className="text-left px-4 py-3 text-dark-300 font-medium">Status</th>
                <th className="text-left px-4 py-3 text-dark-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-dark-400">Loading...</td>
                </tr>
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-dark-400">No invitation codes found</td>
                </tr>
              ) : (
                codes.map((code) => (
                  <tr key={code.id} className="border-t border-dark-600 hover:bg-dark-700">
                    <td className="px-4 py-3">
                      <div className="text-dark-50 font-mono text-sm">{code.code}</div>
                      <div className="text-xs text-dark-500">{code.id}</div>
                    </td>
                    <td className="px-4 py-3 text-dark-200">
                      {code.usedCount ?? 0} / {code.maxUses ?? '∞'}
                      {code.remainingUses !== null && (
                        <div className="text-xs text-dark-400">{code.remainingUses} remaining</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-dark-200">{formatDate(code.expiresAt)}</td>
                    <td className="px-4 py-3 text-dark-200">{code.note || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={code.isValid ? 'text-green-400' : 'text-red-400'}>
                        {code.isValid ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 text-sm">
                        <button
                          onClick={() => navigator.clipboard.writeText(code.code)}
                          className="text-dark-300 hover:text-dark-50"
                        >
                          Copy
                        </button>
                        <button
                          onClick={() => setEditingCode({ ...code })}
                          className="text-primary-400 hover:text-primary-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(code.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingCode && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-dark-800 border border-dark-600 rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-dark-50">Edit Invitation Code</h2>
              <button onClick={() => setEditingCode(null)} className="text-dark-400 hover:text-dark-50">✕</button>
            </div>
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm text-dark-300">Max uses</span>
                <input
                  type="number"
                  min={1}
                  value={editingCode.maxUses ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditingCode({ ...editingCode, maxUses: value === '' ? null : Number(value) });
                  }}
                  className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
                />
              </label>
              <label className="block">
                <span className="text-sm text-dark-300">Expires</span>
                <input
                  type="date"
                  value={editingCode.expiresAt ? editingCode.expiresAt.slice(0, 10) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditingCode({ ...editingCode, expiresAt: value ? new Date(value).toISOString() : null });
                  }}
                  className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
                />
              </label>
              <label className="block">
                <span className="text-sm text-dark-300">Note</span>
                <input
                  type="text"
                  value={editingCode.note || ''}
                  onChange={(e) => setEditingCode({ ...editingCode, note: e.target.value })}
                  className="mt-1 w-full bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditingCode(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleSaveEdit} disabled={isSaving} className="btn-primary disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
